/**
 * Creates a no-op controller stub that mimics the Coveo controller interface.
 * Consumers can safely call .subscribe(), read .state, etc.
 *
 * @param {object} [initialState] - The initial .state value
 * @returns {object} A stub controller
 */
export const createStubController = (initialState = {}) => ({
  state: initialState,
  subscribe: () => () => {},
  toggleSelect: () => {},
  deselectAll: () => {},
  executeFirstSearch: () => {},
  executeFirstRequest: () => {},
  setRanges: () => {},
  // Methods used by content hooks on pager, resultsPerPage, sort
  selectPage: () => {},
  set: () => {},
  sortBy: () => {},
})

/**
 * Creates a stub facetGenerator that matches the Coveo FacetGenerator interface.
 */
export const createStubFacetGenerator = () => ({
  facets: [],
  state: { automaticFacets: [] },
  subscribe: () => () => {},
  deselectAll: () => {},
})

/**
 * Creates a stub search/listing core controller.
 */
export const createStubCore = () => ({
  state: {
    results: [],
    products: [],
    responseId: null,
    isLoading: false,
    error: null,
  },
  subscribe: () => () => {},
  executeFirstSearch: () => {},
  executeFirstRequest: () => {},
  interactiveProduct: () => ({ select: () => {} }),
})

/**
 * Creates a stub pagination controller.
 */
export const createStubPagination = () => ({
  state: { page: 0, pageSize: 50, totalPages: 0 },
  subscribe: () => () => {},
  selectPage: () => {},
  setPageSize: () => {},
})

/**
 * Creates a stub sort controller.
 */
export const createStubSort = () => ({
  state: { appliedSort: { by: 'relevance' } },
  subscribe: () => () => {},
  sortBy: () => {},
})

/**
 * Creates a stub URL manager controller.
 */
export const createStubUrlManager = () => ({
  state: { fragment: '' },
  subscribe: () => () => {},
  synchronize: () => {},
})

/**
 * Creates a stub summary controller.
 */
export const createStubSummary = () => ({
  state: {
    totalNumberOfProducts: 0,
    total: 0,
    isLoading: false,
    firstRequestExecuted: false,
    query: '',
  },
  subscribe: () => () => {},
})

/**
 * Creates a stub breadcrumb manager.
 */
export const createStubBreadcrumbManager = () => ({
  state: { facetBreadcrumbs: [], automaticFacetBreadcrumbs: [] },
  subscribe: () => () => {},
  deselectAll: () => {},
})

/**
 * Creates a stub didYouMean controller.
 */
export const createStubDidYouMean = () => ({
  state: { wasCorrectedTo: '', originalQuery: '' },
  subscribe: () => () => {},
})

/**
 * Creates a stub searchBox controller.
 */
export const createStubSearchBox = () => ({
  state: { value: '' },
  subscribe: () => () => {},
  updateText: () => {},
  submit: () => {},
})

/**
 * Creates a stub cart controller.
 */
export const createStubCart = () => ({
  state: {},
  subscribe: () => () => {},
  updateItemQuantity: () => {},
})

const stubRedirectTrigger = {
  state: { redirectTo: '' },
  subscribe: () => () => {},
}

export const createStubRedirectTrigger = () => stubRedirectTrigger

// Module-level singletons — mirrors the _instance pattern used by initCommerceEngine/initContentEngine.
// Returning the same reference on every call prevents useControllerState from seeing a
// new controller object each render and firing a setState loop when token is absent.
let _stubCommerceInstance = null
let _stubContentInstance = null

/**
 * Builds (once) and returns a stub commerce instance matching initCommerceEngine()'s return shape.
 */
export const createStubCommerceInstance = () => {
  if (_stubCommerceInstance) return _stubCommerceInstance

  const core = createStubCore()
  const pagination = createStubPagination()
  const sort = createStubSort()
  const facetGenerator = createStubFacetGenerator()
  const breadcrumb = createStubBreadcrumbManager()
  const summary = createStubSummary()
  const urlManager = createStubUrlManager()

  _stubCommerceInstance = {
    engine: null,
    searchBox: createStubSearchBox(),
    didYouMean: createStubDidYouMean(),
    cart: createStubCart(),
    controllers: {
      search: { core, pagination, sort, facetGenerator, breadcrumb, summary, urlManager, redirectTrigger: createStubRedirectTrigger() },
      listing: { core, pagination, sort, facetGenerator, breadcrumb, summary, urlManager },
    },
  }
  return _stubCommerceInstance
}

/**
 * Builds (once) and returns a stub content instance matching initContentEngine()'s return shape.
 */
export const createStubContentInstance = () => {
  if (_stubContentInstance) return _stubContentInstance

  _stubContentInstance = {
    engine: createStubController(),
    searchBox: createStubSearchBox(),
    resultList: createStubController({ results: [] }),
    pager: createStubController({ currentPage: 1, maxPage: 1 }),
    resultsPerPage: createStubController({ numberOfResults: 50 }),
    facetManager: createStubController(),
    contentFacets: [],
    facetGenerator: createStubFacetGenerator(),
    breadcrumbManager: createStubBreadcrumbManager(),
    didYouMean: createStubDidYouMean(),
    summary: createStubSummary(),
    urlManager: createStubUrlManager(),
  }
  return _stubContentInstance
}
